import { useState, useEffect, useCallback } from 'react';
import { TextSelection } from '../types/document';

export function useTextSelection() {
  const [selection, setSelection] = useState<TextSelection | null>(null);
  const [protectedSelection, setProtectedSelection] = useState<TextSelection | null>(null);

  const isInDocumentEditor = (element: Node | null): boolean => {
    if (!element) return false;
    
    // 检查是否在文档编辑器内
    let current = element as Element;
    while (current && current.nodeType === Node.ELEMENT_NODE) {
      if (current.classList?.contains('ProseMirror') || 
          current.getAttribute('data-component') === 'document-editor' ||
          current.closest('[data-component="document-editor"]')) {
        return true;
      }
      current = current.parentElement as Element;
    }
    return false;
  };

  const handleSelectionChange = useCallback(() => {
    const sel = window.getSelection();
    
    if (!sel || sel.rangeCount === 0 || sel.toString().trim() === '') {
      // 只有在不是受保护状态时才清空选择
      if (!protectedSelection) {
        setSelection(null);
      }
      return;
    }

    const range = sel.getRangeAt(0);
    const rect = range.getBoundingClientRect();
    
    // 检查选择是否在文档编辑器内
    if (isInDocumentEditor(range.commonAncestorContainer)) {
      console.log('[TextSelection] 忽略文档编辑器内的选择');
      // 如果有受保护的选择，保持不变
      if (protectedSelection) {
        setSelection(protectedSelection);
      }
      return;
    }

    const newSelection: TextSelection = {
      text: sel.toString().trim(),
      range,
      rect
    };
    
    setSelection(newSelection);
    // console.log('[TextSelection] 新的文本选择:', newSelection.text);
  }, [protectedSelection]);

  const clearSelection = useCallback(() => {
    setSelection(null);
    setProtectedSelection(null);
    window.getSelection()?.removeAllRanges();
  }, []);

  const protectSelection = useCallback((sel: TextSelection) => {
    setProtectedSelection(sel);
    setSelection(sel);
    console.log('[TextSelection] 保护选择文本:', sel.text);
  }, []);

  const unprotectSelection = useCallback(() => {
    setProtectedSelection(null);
    console.log('[TextSelection] 取消保护选择文本');
  }, []);

  useEffect(() => {
    document.addEventListener('selectionchange', handleSelectionChange);
    document.addEventListener('mouseup', handleSelectionChange);
    
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
      document.removeEventListener('mouseup', handleSelectionChange);
    };
  }, [handleSelectionChange]);

  return {
    selection,
    clearSelection,
    protectSelection,
    unprotectSelection,
    isProtected: !!protectedSelection
  };
}